import { Link } from 'react-router-dom';
import { ArrowUpRight, HandHeart, Phone, ShieldCheck } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import BookButton from '../components/BookButton';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';
import {
  IMG,
  PHONE,
  PHONE_HREF,
  VA_COMMUNITY_CARE_URL,
  VETERAN_FAQS,
  VETERAN_RESOURCES,
} from '../data/site';

const VA_STEPS = [
  {
    num: '01',
    title: 'Ask your VA provider',
    text: 'Tell your VA primary care team about the pain or tension you\u2019re living with and ask whether a Community Care referral for massage therapy makes sense for you.',
  },
  {
    num: '02',
    title: 'VA issues the authorization',
    text: 'If approved, the VA issues an authorization through the Community Care Network for a set number of visits with a community provider like us.',
  },
  {
    num: '03',
    title: 'We confirm and schedule',
    text: 'Call us with your referral details. We verify the authorization before your first visit — authorized care is billed to the VA, not to you.',
  },
];

export default function Veterans() {
  return (
    <>
      <SEO
        title="Massage for Veterans | VA Community Care — Thula Doula Wellness"
        description="Therapeutic and medical massage for veterans in Humble, TX. VA Community Care referrals accepted, veteran FAQs, and resources. Book online or call (832) 350-8516."
      />
      <PageHeader
        testId="veterans-page-header"
        eyebrow="For Veterans"
        lines={['Care for those', 'who carried the weight.']}
        sub="Therapeutic and medical massage tailored to veterans — including VA Community Care referrals and authorization billing."
      />

      <section className="bg-brand-cream py-16 md:py-24" data-testid="veterans-intro">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <MaskedLinesOnView
              as="h2"
              lines={['Measured, respectful,', 'effective bodywork.']}
              className="font-display text-4xl font-light leading-[1.08] tracking-tight text-brand-ink md:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-brand-moss">
                Chronic pain, service-connected tension, the wear of years of carrying loads —
                physical or otherwise. Our veteran sessions are deliberately conservative at first:
                an unhurried intake covering conditions, medications, and goals, then a first
                session built around learning how your body responds.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-brand-moss">
                You set the pace, the pressure, and the focus at every step. Nothing is rushed, and
                everything is documented so your follow-up sessions build on what worked.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-9 flex flex-wrap gap-4">
                <BookButton testId="veterans-book-btn" />
                <a
                  data-testid="veterans-call-btn"
                  href={PHONE_HREF}
                  className="group inline-flex items-center gap-2.5 rounded-full border border-brand-ink/25 px-7 py-3.5 text-sm font-semibold text-brand-ink transition-[background-color,color,border-color] duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-brand-cream"
                >
                  <Phone className="size-4" /> Call {PHONE}
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <ParallaxImage
                src={IMG.veterans}
                alt="A veteran resting peacefully"
                className="aspect-[4/3] w-full rounded-3xl"
                strength={26}
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-20 md:py-28" data-testid="veterans-va-steps">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['How VA Community Care works.']}
            className="font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-moss">
              If you’re enrolled in VA health care, massage therapy may be available to you in the
              community. Here’s the path, in plain terms:
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
            {VA_STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.12}>
                <div className="border-t-2 border-brand-teal/30 pt-6">
                  <span className="font-display text-5xl font-light italic text-brand-teal/70">
                    {step.num}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-brand-ink">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-moss">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div
              data-testid="veterans-ccn-banner"
              className="mt-14 flex flex-col items-start justify-between gap-6 rounded-3xl bg-brand-ink p-8 text-brand-cream md:flex-row md:items-center md:p-10"
            >
              <div className="flex items-start gap-4">
                <ShieldCheck className="mt-1 size-6 shrink-0 text-brand-sage" />
                <p className="max-w-2xl text-sm leading-relaxed text-brand-cream/85 md:text-base">
                  <span className="font-semibold text-brand-cream">We accept VA Community Care.</span>{' '}
                  Referrals and authorizations can take a week or two — we’re happy to help you
                  check the status. Reach us at {PHONE}.
                </p>
              </div>
              <a
                data-testid="veterans-ccn-link"
                href={VA_COMMUNITY_CARE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-cream px-6 py-3 text-sm font-semibold text-brand-ink transition-[background-color,transform] duration-300 hover:bg-brand-sage active:scale-[0.97]"
              >
                VA Community Care info
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28" data-testid="veterans-support">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <MaskedLinesOnView
              as="h2"
              lines={['What we', 'commonly support.']}
              className="font-display text-4xl font-light leading-[1.08] tracking-tight text-brand-ink md:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-brand-moss">
                Every veteran’s body carries a different history. The concerns we see most often:
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 flex items-center gap-4 rounded-2xl bg-brand-mist/50 p-6">
                <HandHeart className="size-8 shrink-0 text-brand-teal" />
                <p className="text-sm leading-relaxed text-brand-moss">
                  Sessions always coordinate with your VA care team when questions come up — massage
                  works alongside your medical care, never instead of it.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {[
              ['Chronic low-back, neck, and shoulder pain', 'Deep, pattern-focused work for tension that has settled in over years.'],
              ['Service-connected conditions', 'Technique adjusted carefully around your conditions, medications, and provider guidance.'],
              ['Headaches and jaw tension', 'Focused release for tension-type headaches and the neck patterns behind them.'],
              ['Stress, sleep, and nervous-system load', 'Slow, down-regulating sessions that help the body remember what rest feels like.'],
            ].map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="grid grid-cols-1 gap-2 border-b border-brand-ink/10 py-7 sm:grid-cols-12 sm:gap-6">
                  <h3 className="font-display text-2xl font-light tracking-tight text-brand-ink sm:col-span-5">
                    {title}
                  </h3>
                  <p className="text-sm leading-relaxed text-brand-moss sm:col-span-7">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-20 md:py-28" data-testid="veterans-resources">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['Resources for veterans.']}
            className="font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
          />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {VETERAN_RESOURCES.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1}>
                <a
                  data-testid={`veterans-resource-${i}`}
                  href={r.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col justify-between rounded-3xl bg-brand-cream p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_-30px_rgba(19,42,38,0.4)]"
                >
                  <div>
                    <h3 className="font-display text-2xl font-light tracking-tight text-brand-ink">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-moss">{r.desc}</p>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">
                    {r.linkLabel}
                    <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28" data-testid="veterans-faqs">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <MaskedLinesOnView
              as="h2"
              lines={['Veteran', <span key="i" className="italic">questions.</span>]}
              className="font-display text-4xl font-light leading-[1.08] tracking-tight text-brand-ink md:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-sm text-base leading-relaxed text-brand-moss">
                Something we didn’t cover? Call us at {PHONE} — we’ll talk it through, no
                obligation.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {VETERAN_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`vfaq-${i}`} className="border-brand-ink/10">
                  <AccordionTrigger
                    data-testid={`veteran-faq-trigger-${i}`}
                    className="py-6 text-left font-display text-xl font-normal tracking-tight text-brand-ink hover:text-brand-teal hover:no-underline md:text-2xl"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm leading-relaxed text-brand-moss md:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 text-center md:px-10">
          <MaskedLinesOnView
            as="h2"
            lines={['Ready when you are.']}
            className="font-display text-[clamp(2.75rem,7vw,5.5rem)] font-light italic leading-none tracking-tight text-brand-ink"
          />
          <Reveal delay={0.2}>
            <div className="mt-10 flex justify-center">
              <BookButton size="lg" testId="veterans-cta-book-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
